import { Grid, Typography } from "@mui/material";
import createStyles from "@mui/styles/createStyles";
import makeStyles from "@mui/styles/makeStyles";
import { ExternalLink } from "ExternalLink";
import { OLButton } from "OLButton";
import { EmptyState } from "components/EmptyState/EmptyState";
import { FormSection } from "components/FormControls/FormSection";
import { ReactComponent as FilePlus03Icon } from "components/Icons/file-plus-03.svg";
import { OLTheme } from "contexts/Theme/ThemeProvider";
import { FieldArray } from "formik";
import { useMessage } from "hooks/intl";
import { AttributeType } from "types/customEvents";
import { Attribute } from "./Attribute";

export const useStyles = makeStyles((theme: OLTheme) =>
  createStyles({
    deleteWrapper: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      borderRadius: "50%",
      backgroundColor: theme.gray100,
      width: "32px",
      minWidth: "32px",
      height: "32px",
      cursor: "pointer",
    },
    subTitle: {
      fontSize: "16px",
      fontWeight: 400,
      color: theme.gray500,
      textAlign: "center",
      maxWidth: "400px",
    },
    buttonWrapper: {
      marginTop: theme.spacing(12),
      display: "flex",
      justifyContent: "flex-end",

      "& button": {
        marginLeft: theme.spacing(8),
      },
    },
    attribute: {
      background: theme.gray50,
      padding: theme.spacing(8),
      border: `1px solid ${theme.gray200}`,
      borderRadius: "4px",
    },
    input: {
      "& input": { background: theme.white00 },
      background: theme.white00,
    },
    error: {
      color: theme.red500,
      display: "block",
      fontSize: 14,
      marginBottom: theme.spacing(8),
      "& + &": {
        marginTop: 0,
      },
    },
    displayNone: {
      display: "none",
      "&.error-edit-display": {
        display: "block",
      },
    },
    link: {
      color: theme.primary500,
      textDecoration: "underline",
    },
    titleWrapper: { display: "flex", justifyContent: "space-between" },
    previewCode: {
      color: theme.primary500,
      textTransform: "uppercase",
      fontWeight: 500,
      fontSize: 15,
      cursor: "pointer",
    },
    icon: {
      color: theme.primary500,
    },
  }),
);

interface Props {
  attributes?: AttributeType[];
  setFieldValue: (field: string, value: string) => void;
  setFieldError: (field: string, errorMsg?: any) => void;
  errors?: any;
}

export const Attributes = ({
  attributes,
  setFieldValue,
  setFieldError,
  errors,
}: Props) => {
  const { message } = useMessage();
  const classes = useStyles();
  const isAnyAttributeAdded = attributes && attributes.length > 0;

  return (
    <FormSection
      title={message({ id: "attributes" })}
      subTitle={
        <Typography
          data-test-id="campaign-rules-error-general-edit"
          id="data-rules-edit"
          className={`${classes.error} ${classes.displayNone} Mui-error`}
        >
          {message({ id: "toAddEventSchemaFinisheEditing" })}
        </Typography>
      }
      sectionError={errors}
    >
      <FieldArray
        name="schema.fields"
        render={(arrayHelpers) => (
          <>
            {isAnyAttributeAdded ? (
              <>
                <Grid container columnSpacing={16} rowSpacing={4}>
                  {attributes.map((attribute, index) => {
                    const attributeError = errors && errors.fields?.[index];
                    const isComplexAttribute =
                      attribute.type === "object" ||
                      attribute.type === "collection";

                    return (
                      <Grid item xs={12} key={index}>
                        <Attribute
                          remove={arrayHelpers.remove}
                          add={arrayHelpers.unshift}
                          index={index}
                          attribute={attribute}
                          setFieldValue={setFieldValue}
                          setFieldError={setFieldError}
                          error={attributeError}
                          isComplexAttribute={isComplexAttribute}
                        />
                      </Grid>
                    );
                  })}
                  <Grid item xs={12} mt={6}>
                    <OLButton
                      variant="contained"
                      data-test-id="add-attribute"
                      type="button"
                      onClick={() =>
                        arrayHelpers.push({
                          draft: {
                            name: "",
                            description: "",
                          },
                          isSaved: false,
                          isEditing: true,
                        })
                      }
                    >
                      {message({ id: "addAttribute" })}
                    </OLButton>
                  </Grid>
                </Grid>
              </>
            ) : (
              <EmptyState
                icon={<FilePlus03Icon className={classes.icon} />}
                title={message({ id: "addFirstAttribute" })}
                description={message(
                  { id: "customSchemaDesc" },
                  {
                    eventBasedCampaign: (
                      <ExternalLink
                        className={`${classes.subTitle} ${classes.link}`}
                        path="https://help.openloyalty.io/main-features/members/custom-events/custom-event-schemas"
                      >
                        {message({
                          id: "eventBasedCampaign",
                        })}
                      </ExternalLink>
                    ),
                  },
                )}
                button={
                  <OLButton
                    variant="contained"
                    type="button"
                    size="medium"
                    data-test-id="add-attribute"
                    onClick={() =>
                      arrayHelpers.push({
                        draft: {
                          name: "",
                          description: "",
                        },
                        isSaved: false,
                        isEditing: true,
                      })
                    }
                  >
                    {message({ id: "addAttribute" })}
                  </OLButton>
                }
              />
            )}
          </>
        )}
      />
    </FormSection>
  );
};
